import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Landmark, Send, ShieldCheck, CheckCircle2, XCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Panel, Stat, StatusPill, SandboxNotice, fmtMoney, fmtDate } from "@/components/Peep";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

function Balances({ b, testid }) {
  return (
    <span data-testid={testid} className="text-xs text-[#a7a0af]">
      pending {fmtMoney(b.pending)} · available <span className="text-white">{fmtMoney(b.available)}</span> · in payout {fmtMoney(b.in_payout)} · paid {fmtMoney(b.paid)}
    </span>
  );
}

function CreatorTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table data-testid="finance-creator-table" className="w-full min-w-[760px] text-left text-sm">
        <thead className="text-[0.65rem] uppercase tracking-wider text-[#a7a0af]">
          <tr><th className="py-2 pr-3">Creator</th><th className="pr-3">Gross</th><th className="pr-3">Subs</th><th className="pr-3">Tips</th><th className="pr-3">PPV</th><th className="pr-3">Refunds</th><th className="pr-3">Creator 60%</th><th className="pr-3">Available</th><th>Bank</th></tr>
        </thead>
        <tbody>
          {rows.map(({ creator: c, revenue: r, by_type: t, balances: b, account: a }) => (
            <tr key={c.id} data-testid={`finance-row-${c.handle}`} className="border-t border-white/10">
              <td className="py-2 pr-3 font-semibold">{c.display_name} <span className="text-xs text-[#6f6878]">{c.status}</span></td>
              <td className="pr-3">{fmtMoney(r.gross)}</td>
              <td className="pr-3">{fmtMoney(t.subscription.gross)}</td>
              <td className="pr-3">{fmtMoney(t.tip.gross)}</td>
              <td className="pr-3">{fmtMoney(t.ppv.gross)}</td>
              <td className="pr-3">{fmtMoney(r.refunds + r.chargebacks)}</td>
              <td className="pr-3 text-[#f0a8fa]">{fmtMoney(r.earnings)}</td>
              <td className="pr-3">{fmtMoney(b.available)}</td>
              <td className="text-xs">{a ? <StatusPill status={a.payouts_enabled ? "verified" : a.status} /> : <span className="text-[#6f6878]">not connected</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PayoutList({ payouts, onSettle }) {
  if (payouts.length === 0) return <p data-testid="payouts-empty" className="text-sm text-[#a7a0af]">No payouts yet.</p>;
  return payouts.map((p) => (
    <div key={p.id} data-testid={`payout-row-${p.id}`} className="flex flex-wrap items-center justify-between gap-2 border-t border-white/5 py-2 text-sm">
      <span>{p.beneficiary_name} · {fmtMoney(p.amount)} <span className="text-xs text-[#6f6878]">{p.entry_count} entries · {p.bank_label} · {fmtDate(p.created_at)}</span></span>
      <span className="flex items-center gap-2">
        <StatusPill status={p.status} />
        {p.status === "processing" && p.sandbox && (
          <>
            <button onClick={() => onSettle(p, "paid")} data-testid={`payout-paid-${p.id}`} className="flex items-center gap-1 rounded-lg border border-emerald-400/30 px-2 py-1 text-xs text-emerald-300"><CheckCircle2 size={12} /> Sim. paid</button>
            <button onClick={() => onSettle(p, "failed")} data-testid={`payout-failed-${p.id}`} className="flex items-center gap-1 rounded-lg border border-red-400/30 px-2 py-1 text-xs text-red-300"><XCircle size={12} /> Sim. failed</button>
          </>
        )}
      </span>
    </div>
  ));
}

export default function AdminFinance() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [rec, setRec] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/finance").then((r) => setD(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const run = async () => {
    try {
      const { data } = await api.post("/admin/payouts/run", { scope: "all" });
      toast.success(`${data.created} payout(s) created`);
      load();
    } catch (e) { err(e); }
  };
  const settle = async (p, outcome) => {
    try {
      await api.post(`/admin/payouts/${p.id}/simulate`, { outcome });
      load();
    } catch (e) { err(e); }
  };
  const connect = async () => {
    try {
      const { data } = await api.post("/admin/platform-payout/connect");
      navigate(data.onboarding_url);
    } catch (e) { err(e); }
  };
  const reconcile = () => api.get("/admin/finance/reconciliation").then((r) => setRec(r.data)).catch(err);
  if (!d) return <p className="text-sm text-[#a7a0af]">Loading…</p>;
  const t = d.totals;
  const pa = d.platform.account;
  return (
    <div data-testid="admin-finance" className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl font-extrabold">Finance</h1>
        <p className="mt-1 text-sm text-[#a7a0af]">Creator {d.split.creator_pct}% · PEEPING {d.split.platform_pct}% — calculated on the server, {d.split.basis}. Payout hold {d.hold_days} days.</p>
      </div>
      <SandboxNotice />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Gross revenue" value={fmtMoney(t.gross)} testid="fin-gross" />
        <Stat label="PEEPING 40%" value={fmtMoney(t.platform_share)} testid="fin-platform" />
        <Stat label="Creators 60%" value={fmtMoney(t.creator_share)} testid="fin-creators" />
        <Stat label="Unallocated All Access (60% pool)" value={fmtMoney(t.unallocated_creator_pool)} testid="fin-unallocated" />
        <Stat label="Subscriptions" value={fmtMoney(d.by_type.subscription.gross)} testid="fin-subs" />
        <Stat label="Tips" value={fmtMoney(d.by_type.tip.gross)} testid="fin-tips" />
        <Stat label="Pay-per-view" value={fmtMoney(d.by_type.ppv.gross)} testid="fin-ppv" />
        <Stat label="Refunds / disputes" value={`${fmtMoney(t.refunds)} / ${fmtMoney(t.chargebacks)}`} testid="fin-refunds" />
        <Stat label="Processing fees" value={fmtMoney(t.processing_fees)} />
        <Stat label="Payouts processing" value={`${d.payouts_summary.processing.count} · ${fmtMoney(d.payouts_summary.processing.amount)}`} testid="fin-po-processing" />
        <Stat label="Payouts completed" value={`${d.payouts_summary.paid.count} · ${fmtMoney(d.payouts_summary.paid.amount)}`} testid="fin-po-paid" />
        <Stat label="Payouts failed" value={d.payouts_summary.failed.count} />
      </div>
      <Panel title="PEEPING platform payout account" action={<Landmark size={14} className="text-[#a7a0af]" />}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col gap-1">
            <p data-testid="platform-account-status" className="text-sm">{pa ? `${pa.bank_label || "Bank not verified"} · ${pa.status}` : "Not connected"}</p>
            <Balances b={d.platform.balances} testid="platform-balances" />
          </div>
          {!pa?.payouts_enabled && <button onClick={connect} data-testid="platform-connect-btn" className="btn-brand rounded-xl px-4 py-2 text-xs font-semibold">Connect platform bank</button>}
        </div>
      </Panel>
      <Panel title={`Creators · ${d.by_creator.length} / ${d.max_active_creators} max`}>
        {d.by_creator.length === 0 ? <p className="text-sm text-[#a7a0af]">No creators yet.</p> : <CreatorTable rows={d.by_creator} />}
      </Panel>
      <Panel title="Payouts" action={<button onClick={run} data-testid="run-payouts-btn" className="btn-brand flex items-center gap-1 rounded-xl px-3 py-1.5 text-xs font-semibold"><Send size={12} /> Run payouts</button>}>
        <p className="mb-2 text-xs text-[#a7a0af]">Creates one payout per verified account from its available balance (min {fmtMoney(d.min_payout)}). Each ledger entry can only be paid once.</p>
        <PayoutList payouts={d.payouts} onSettle={settle} />
      </Panel>
      <Panel title="Refunds & disputes">
        {d.disputes.length === 0 ? <p data-testid="disputes-empty" className="text-sm text-[#a7a0af]">None.</p> : d.disputes.map((x) => (
          <div key={x.id} className="flex justify-between border-t border-white/5 py-2 text-sm">
            <span className="capitalize">{x.type} · {x.plan_name} <span className="text-xs text-[#6f6878]">{x.creator_name} · {x.revenue_type}</span></span>
            <span>{fmtMoney(x.amount)} · {fmtDate(x.created_at)}</span>
          </div>
        ))}
      </Panel>
      <Panel title="Reconciliation" action={<button onClick={reconcile} data-testid="reconcile-btn" className="flex items-center gap-1 rounded-xl border border-white/15 px-3 py-1.5 text-xs"><ShieldCheck size={12} /> Run check</button>}>
        {!rec ? <p className="text-sm text-[#a7a0af]">Verifies ledger math, the 60/40 split, charge totals and payout claims.</p> : (
          <div data-testid="reconcile-result" className="text-sm">
            <p className={rec.ok ? "text-emerald-300" : "text-amber-300"}>{rec.ok ? "All balanced" : `${rec.issue_count} issue(s) found`} · {rec.checked.ledger_entries} entries, {rec.checked.payments} payments, {rec.checked.payouts} payouts</p>
            {rec.issues.map((i) => <p key={i} className="text-xs text-[#a7a0af]">{i}</p>)}
          </div>
        )}
      </Panel>
    </div>
  );
}
